import classNames from 'classnames';
import { ReactNode, useMemo, useState } from 'react';
import Image from '@alltrails/core/components/Image';
import type { LinkInfo, Size } from '../../types';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkInfoRenderer from '../LinkInfoRenderer';
import styles from './DynamicButton.module.scss';

const dynamicImageSizes: Record<DynamicButtonSizes, number> = {
  md: 32
};

const dynamicIconSizes: Record<DynamicButtonSizes, Size<'xs'>> = {
  md: 'xs'
};

type DynamicButtonSizes = Size<'md'>;
type DynamicButtonProps = {
  className?: string;
  disabled?: boolean;
  iconFallback?: IconDefinition<'orientation' | 'color'>;
  imageAlt?: string;
  linkInfo?: LinkInfo;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  size?: DynamicButtonSizes;
  src?: string;
  testId?: string;
  text?: ReactNode;
  title?: string;
};

const DynamicButton = ({
  className,
  disabled,
  iconFallback,
  imageAlt,
  linkInfo,
  onClick,
  size = 'md',
  src,
  testId,
  text,
  title
}: DynamicButtonProps) => {
  const [imageError, setImageError] = useState(false);
  const classes = classNames(className, styles.button, styles[size], styles.elevated, {
    [styles.disabled]: disabled,
    [styles.link]: !!linkInfo && !disabled,
    [styles.noDynamicContent]: !src && !iconFallback,
    [styles.noText]: !text
  });

  const content = useMemo(() => {
    if (src && !imageError) {
      return (
        <>
          <Image
            alt={imageAlt ?? ''}
            className={styles.dynamicImage}
            height={dynamicImageSizes[size]}
            onError={() => setImageError(true)}
            src={src}
            width={dynamicImageSizes[size]}
          />
          <span>{text}</span>
        </>
      );
    }

    if (iconFallback) {
      return (
        <>
          <div className={styles.dynamicImage}>
            <IconRenderer icon={iconFallback} defaults={{ color: '--color-neutral-white', size: dynamicIconSizes[size] }} />
          </div>
          <span>{text}</span>
        </>
      );
    }

    return <span>{text}</span>;
  }, [iconFallback, imageAlt, imageError, size, src, text]);

  if (linkInfo) {
    return (
      <LinkInfoRenderer className={classes} linkInfo={linkInfo} onClick={onClick} testId={testId} title={title}>
        {content}
      </LinkInfoRenderer>
    );
  }

  return (
    <button className={classes} data-testid={testId} disabled={disabled} onClick={onClick}>
      {content}
    </button>
  );
};

export default DynamicButton;
